import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import { RepairRequest, RepairStatus } from '../types/firestore';
import type { RepairRequestPhotoAsset } from '../types/firestore';
import type { FinalRepairQuoteLineItem, PreliminaryEstimate } from '../types/firestore';
import type { ServiceCancellationReasonCode } from '../types/firestore';
import {
  RepairRequestCreateInput,
  type RepairRequestPriorityRequestCreateInput,
  type RepairRequestFulfillmentCreateSelection,
  hasConflictingLegacyServiceId,
  normalizeServiceProviderIdForCreate,
} from '../shared/repairRequestCreatePayload';
import {
  canTransitionRepairRequest,
  getActiveRepairStatuses,
  getTransitionAction,
  type RepairRequestWorkflowActor,
} from '../utils/repairRequestWorkflow';
import { type RepairRequestUnreadSection } from '../utils/repairRequestReadModel';

// Types for filters and sorting
export type StatusFilter = RepairStatus | 'active' | 'all';
export type SortByType = 'createdAt' | 'status' | 'updatedAt';

const getTimestampMs = (value: any): number => {
  if (!value) return 0;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  return 0;
};

const sortRequests = (requests: RepairRequest[], sortBy: SortByType): RepairRequest[] => {
  if (sortBy === 'status') {
    return [...requests].sort((a, b) => (a.status || '').localeCompare(b.status || ''));
  }

  const field = sortBy === 'updatedAt' ? 'updatedAt' : 'createdAt';
  return [...requests].sort(
    (a, b) => getTimestampMs((b as any)[field]) - getTimestampMs((a as any)[field])
  );
};

const filterRequestsByStatus = (
  requests: RepairRequest[],
  statusFilter: StatusFilter
): RepairRequest[] => {
  if (statusFilter === 'active') {
    const activeStatuses = new Set(getActiveRepairStatuses());
    return requests.filter((request) => activeStatuses.has(request.status));
  }
  if (statusFilter !== 'all') {
    return requests.filter((request) => request.status === statusFilter);
  }
  return requests;
};

const dedupeRequests = (requests: RepairRequest[]): RepairRequest[] => {
  const byId = new Map<string, RepairRequest>();
  requests.forEach((request) => {
    const requestId = String(request.uid || request.id || '').trim();
    if (!requestId) return;
    byId.set(requestId, request);
  });
  return Array.from(byId.values());
};

const applyRequestListView = (
  requests: RepairRequest[],
  statusFilter: StatusFilter,
  sortBy: SortByType
): RepairRequest[] => sortRequests(filterRequestsByStatus(requests, statusFilter), sortBy);

const snapshotToRequests = (querySnapshot: { forEach: (callback: (doc: any) => void) => void }) => {
  const requests: RepairRequest[] = [];
  querySnapshot.forEach((docSnap) => {
    requests.push({ uid: docSnap.id, ...docSnap.data() } as RepairRequest);
  });
  return requests;
};

const rethrowCallableError = (error: unknown, fallback: string): never => {
  if (error instanceof Error) {
    throw error;
  }

  throw new Error(fallback);
};

export type ServiceScheduleTimestampInput =
  | string
  | number
  | Date
  | {
      toDate?: () => Date;
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

export interface ScheduleRepairRequestServicePlanPayload {
  scheduledStartAt: ServiceScheduleTimestampInput;
  scheduledEndAt?: ServiceScheduleTimestampInput | null;
  note?: string | null;
}

type ScheduleRepairRequestServicePlanCallableInput = {
  requestId: string;
  scheduledStartAt: string;
  scheduledEndAt?: string;
  timezone: 'Europe/Warsaw';
  note?: string;
};

type ScheduleRepairRequestServicePlanCallableResponse = {
  success: boolean;
  requestId: string;
  previousStatus: string;
  serviceScheduleStatus: 'scheduled';
  previousServiceScheduleStatus: string | null;
};

type ClearRepairRequestServicePlanCallableResponse = {
  success: boolean;
  requestId: string;
  previousStatus: string;
  serviceScheduleStatus: 'cleared';
  previousServiceScheduleStatus: string | null;
};

const SERVICE_SCHEDULE_TIMEZONE = 'Europe/Warsaw' as const;
const SERVICE_SCHEDULE_NOTE_MAX_LENGTH = 500;

const normalizeRequestIdForCommand = (requestId: string): string => {
  const normalized = String(requestId || '').trim();
  if (!normalized) {
    throw new Error('Nie można wykonać akcji bez identyfikatora zgłoszenia.');
  }
  return normalized;
};

const parseServiceScheduleInputDate = (
  value: ServiceScheduleTimestampInput | null | undefined,
  fieldLabel: string
): Date => {
  if (value == null || value === '') {
    throw new Error(
      fieldLabel === 'scheduledStartAt'
        ? 'Podaj początek terminu pracy.'
        : 'Podaj poprawny koniec terminu pracy.'
    );
  }

  if (value instanceof Date) {
    if (Number.isFinite(value.getTime())) {
      return value;
    }
    throw new Error('Termin pracy musi być poprawną datą.');
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (Number.isFinite(date.getTime())) {
      return date;
    }
    throw new Error('Termin pracy musi być poprawną datą.');
  }

  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    if (date instanceof Date && Number.isFinite(date.getTime())) {
      return date;
    }
  }

  if (typeof value.toMillis === 'function') {
    const date = new Date(value.toMillis());
    if (Number.isFinite(date.getTime())) {
      return date;
    }
  }

  const seconds = value.seconds ?? value._seconds;
  const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
  if (
    Number.isInteger(seconds) &&
    Number.isInteger(nanoseconds) &&
    (nanoseconds as number) >= 0 &&
    (nanoseconds as number) <= 999999999
  ) {
    const date = new Date((seconds as number) * 1000 + Math.floor((nanoseconds as number) / 1e6));
    if (Number.isFinite(date.getTime())) {
      return date;
    }
  }

  throw new Error('Termin pracy musi być poprawną datą.');
};

const normalizeServiceScheduleNote = (note: unknown): string | undefined => {
  if (note == null) {
    return undefined;
  }
  if (typeof note !== 'string') {
    throw new Error('Notatka planera musi być tekstem.');
  }

  const normalized = note.trim();
  if (!normalized) {
    return undefined;
  }
  if (normalized.length > SERVICE_SCHEDULE_NOTE_MAX_LENGTH) {
    throw new Error(
      `Notatka planera może mieć maksymalnie ${SERVICE_SCHEDULE_NOTE_MAX_LENGTH} znaków.`
    );
  }

  return normalized;
};

const normalizeScheduleRepairRequestServicePlanPayload = (
  payload: ScheduleRepairRequestServicePlanPayload
): Omit<ScheduleRepairRequestServicePlanCallableInput, 'requestId'> => {
  const scheduledStartAt = parseServiceScheduleInputDate(
    payload?.scheduledStartAt,
    'scheduledStartAt'
  );
  const scheduledEndAt =
    payload?.scheduledEndAt == null
      ? null
      : parseServiceScheduleInputDate(payload.scheduledEndAt, 'scheduledEndAt');

  if (scheduledEndAt && scheduledEndAt.getTime() <= scheduledStartAt.getTime()) {
    throw new Error('Koniec terminu pracy musi być po początku.');
  }

  const note = normalizeServiceScheduleNote(payload?.note);

  return {
    scheduledStartAt: scheduledStartAt.toISOString(),
    ...(scheduledEndAt ? { scheduledEndAt: scheduledEndAt.toISOString() } : {}),
    timezone: SERVICE_SCHEDULE_TIMEZONE,
    ...(note ? { note } : {}),
  };
};

const mapServicePlannerCallableError = (error: unknown, fallback: string): Error => {
  const code = String((error as { code?: unknown })?.code || '');
  const rawMessage = String((error as { message?: unknown })?.message || '').toLowerCase();

  if (code.includes('permission-denied') || rawMessage.includes('permission-denied')) {
    return new Error('Nie masz uprawnień do planowania tej sprawy.');
  }
  if (code.includes('failed-precondition') && rawMessage.includes('terminal')) {
    return new Error('Tej sprawy nie można już planować.');
  }
  if (code.includes('invalid-argument') || rawMessage.includes('scheduled')) {
    return new Error('Nie udało się zapisać terminu pracy. Sprawdź datę i spróbuj ponownie.');
  }

  return new Error(fallback);
};

/**
 * Gets details of a specific repair request
 */
const getRequestDetails = async (requestId: string): Promise<RepairRequest> => {
  try {
    const docSnap = await getDoc(doc(firestore, 'repairRequests', requestId));

    if (!docSnap.exists()) {
      throw new Error('Nie znaleziono zlecenia');
    }

    return { uid: docSnap.id, ...docSnap.data() } as RepairRequest;
  } catch (error) {
    console.error('Error fetching request details:', error);
    throw error;
  }
};

/**
 * Creates a new repair request
 */
const createRepairRequest = async (requestData: RepairRequestCreateInput): Promise<string> => {
  type CreateRepairRequestCallableInput = {
    clientRequestId?: string;
    serviceProviderId: string;
    manualDeviceType?: string;
    brand?: string;
    model?: string;
    description?: string;
    urgency?: string;
    scheduledDate?: any;
    photos?: string[];
    clientNotes?: string;
    location?: RepairRequest['location'];
    clientDetails?: RepairRequest['clientDetails'];
    fulfillmentSelection?: RepairRequestFulfillmentCreateSelection;
    priorityRequest?: RepairRequestPriorityRequestCreateInput;
  };

  type CreateRepairRequestCallableResponse = {
    success: boolean;
    requestId: string;
    requestIndex: string;
    serviceOperationalIndex?: string;
  };

  const resolvedServiceProviderId = normalizeServiceProviderIdForCreate(requestData);

  if (!requestData.clientId || !resolvedServiceProviderId) {
    console.error('[createRepairRequest] Missing required fields:', {
      clientId: !requestData.clientId,
      serviceProviderId: !resolvedServiceProviderId,
    });
    throw new Error('Missing required fields: clientId or serviceProviderId');
  }

  if (hasConflictingLegacyServiceId(requestData)) {
    throw new Error('Niespójny identyfikator serwisu. Odśwież stronę i spróbuj ponownie.');
  }

  try {
    const createRepairRequestFn = httpsCallable<
      CreateRepairRequestCallableInput,
      CreateRepairRequestCallableResponse
    >(functions, 'createRepairRequest');

    const payload: CreateRepairRequestCallableInput = {
      ...(requestData.clientRequestId ? { clientRequestId: requestData.clientRequestId } : {}),
      serviceProviderId: resolvedServiceProviderId,
      ...(requestData.manualDeviceType ? { manualDeviceType: requestData.manualDeviceType } : {}),
      ...(requestData.brand ? { brand: requestData.brand } : {}),
      ...(requestData.model ? { model: requestData.model } : {}),
      ...(requestData.description ? { description: requestData.description } : {}),
      ...(requestData.urgency ? { urgency: requestData.urgency } : {}),
      ...(requestData.scheduledDate ? { scheduledDate: requestData.scheduledDate } : {}),
      ...(requestData.photos ? { photos: requestData.photos } : {}),
      ...(requestData.clientNotes ? { clientNotes: requestData.clientNotes } : {}),
      ...(requestData.location ? { location: requestData.location } : {}),
      ...(requestData.clientDetails ? { clientDetails: requestData.clientDetails } : {}),
      ...(requestData.fulfillmentSelection
        ? { fulfillmentSelection: requestData.fulfillmentSelection }
        : {}),
      ...(requestData.priorityRequest?.requested
        ? {
            priorityRequest: {
              requested: true,
              acceptsPotentialExtraFee:
                requestData.priorityRequest.acceptsPotentialExtraFee === true,
              ...(requestData.priorityRequest.note?.trim()
                ? { note: requestData.priorityRequest.note.trim() }
                : {}),
            },
          }
        : {}),
    };

    const result = await createRepairRequestFn(payload);
    const requestId = result.data?.requestId;
    if (!requestId) {
      throw new Error('Backend nie zwrócił identyfikatora zgłoszenia.');
    }

    console.log('Created repair request with callable function:', requestId);
    return requestId;
  } catch (error) {
    console.error('Error creating repair request with callable:', error);
    return rethrowCallableError(error, 'Nie udało się utworzyć zgłoszenia.');
  }
};

const attachRequestPhotos = async (
  requestId: string,
  photoAssets: Array<Omit<RepairRequestPhotoAsset, 'createdAt' | 'createdBy'>>
): Promise<void> => {
  type AttachRequestPhotosCallableInput = {
    requestId: string;
    photoAssets: Array<Omit<RepairRequestPhotoAsset, 'createdAt' | 'createdBy'>>;
  };
  type AttachRequestPhotosCallableResponse = {
    success: boolean;
    requestId: string;
    photoCount: number;
  };

  if (!requestId || photoAssets.length === 0) {
    return;
  }

  const attachRequestPhotosFn = httpsCallable<
    AttachRequestPhotosCallableInput,
    AttachRequestPhotosCallableResponse
  >(functions, 'attachRequestPhotos');

  await attachRequestPhotosFn({ requestId, photoAssets });
};

const subscribeToRequestDetails = (
  requestId: string,
  onNext: (request: RepairRequest) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const requestRef = doc(firestore, 'repairRequests', requestId);
  return onSnapshot(
    requestRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        return;
      }
      onNext({ uid: docSnap.id, ...docSnap.data() } as RepairRequest);
    },
    (error) => {
      console.error('Error subscribing to repair request details:', error);
      onError?.(error as Error);
    }
  );
};

const startRepairRequestQuote = async (requestId: string): Promise<void> => {
  type StartRepairRequestQuoteCallableInput = {
    requestId: string;
  };
  type StartRepairRequestQuoteCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'pendingQuote';
  };

  const startRepairRequestQuoteFn = httpsCallable<
    StartRepairRequestQuoteCallableInput,
    StartRepairRequestQuoteCallableResponse
  >(functions, 'startRepairRequestQuote');

  try {
    await startRepairRequestQuoteFn({ requestId });
  } catch (error) {
    console.error('Error starting repair quote with callable:', error);
    return rethrowCallableError(error, 'Nie udało się rozpocząć wyceny.');
  }
};

const submitRepairRequestQuote = async (
  requestId: string,
  quotedPrice: number,
  quoteDescription: string
): Promise<void> => {
  type SubmitRepairRequestQuoteCallableInput = {
    requestId: string;
    quotedPrice: number;
    quoteDescription?: string;
  };
  type SubmitRepairRequestQuoteCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'quoted';
  };

  const submitRepairRequestQuoteFn = httpsCallable<
    SubmitRepairRequestQuoteCallableInput,
    SubmitRepairRequestQuoteCallableResponse
  >(functions, 'submitRepairRequestQuote');

  try {
    await submitRepairRequestQuoteFn({
      requestId,
      quotedPrice,
      quoteDescription: quoteDescription.trim(),
    });
  } catch (error) {
    console.error('Error submitting repair quote with callable:', error);
    return rethrowCallableError(error, 'Nie udało się wysłać wyceny.');
  }
};

const respondToRepairQuote = async (
  requestId: string,
  decision: 'accept' | 'reject'
): Promise<void> => {
  type RespondToRepairQuoteCallableInput = {
    requestId: string;
    decision: 'accept' | 'reject';
  };
  type RespondToRepairQuoteCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'accepted' | 'rejected';
  };

  const respondToRepairQuoteFn = httpsCallable<
    RespondToRepairQuoteCallableInput,
    RespondToRepairQuoteCallableResponse
  >(functions, 'respondToRepairQuote');

  try {
    await respondToRepairQuoteFn({ requestId, decision });
  } catch (error) {
    console.error('Error responding to repair quote with callable:', error);
    return rethrowCallableError(error, 'Nie udało się zapisać decyzji o wycenie.');
  }
};

export type SubmitPreliminaryEstimatePayload = Pick<
  PreliminaryEstimate,
  'diagnosisFee' | 'pickupOrTravelFee' | 'priceRangeMin' | 'priceRangeMax' | 'comment'
>;

const submitPreliminaryEstimate = async (
  requestId: string,
  payload: SubmitPreliminaryEstimatePayload
): Promise<void> => {
  type SubmitPreliminaryEstimateCallableInput = SubmitPreliminaryEstimatePayload & {
    requestId: string;
  };
  type SubmitPreliminaryEstimateCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'preliminaryQuoted';
  };

  const submitPreliminaryEstimateFn = httpsCallable<
    SubmitPreliminaryEstimateCallableInput,
    SubmitPreliminaryEstimateCallableResponse
  >(functions, 'submitPreliminaryEstimate');

  try {
    await submitPreliminaryEstimateFn({
      requestId,
      ...payload,
      ...(payload.comment ? { comment: payload.comment.trim() } : {}),
    });
  } catch (error) {
    console.error('Error submitting preliminary estimate with callable:', error);
    return rethrowCallableError(error, 'Nie udało się wysłać wyceny wstępnej.');
  }
};

const respondToPreliminaryEstimate = async (
  requestId: string,
  decision: 'accept' | 'reject'
): Promise<void> => {
  type RespondToPreliminaryEstimateCallableInput = {
    requestId: string;
    decision: 'accept' | 'reject';
  };
  type RespondToPreliminaryEstimateCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'awaitingFulfillment' | 'preliminaryRejected';
  };

  const respondToPreliminaryEstimateFn = httpsCallable<
    RespondToPreliminaryEstimateCallableInput,
    RespondToPreliminaryEstimateCallableResponse
  >(functions, 'respondToPreliminaryEstimate');

  try {
    await respondToPreliminaryEstimateFn({ requestId, decision });
  } catch (error) {
    console.error('Error responding to preliminary estimate with callable:', error);
    return rethrowCallableError(error, 'Nie udało się zapisać decyzji o wycenie wstępnej.');
  }
};

const confirmRepairRequestFulfillment = async (requestId: string, note?: string): Promise<void> => {
  type ConfirmRepairRequestFulfillmentCallableInput = {
    requestId: string;
    note?: string;
  };
  type ConfirmRepairRequestFulfillmentCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'diagnosisInProgress';
    mode: 'customerDropoff' | 'servicePickup' | 'onsiteService';
    eventType: 'customerDropoffReceived' | 'servicePickupCompleted' | 'onsiteVisitStarted';
  };

  const confirmRepairRequestFulfillmentFn = httpsCallable<
    ConfirmRepairRequestFulfillmentCallableInput,
    ConfirmRepairRequestFulfillmentCallableResponse
  >(functions, 'confirmRepairRequestFulfillment');

  try {
    const normalizedNote = note?.trim();
    await confirmRepairRequestFulfillmentFn({
      requestId,
      ...(normalizedNote ? { note: normalizedNote } : {}),
    });
  } catch (error) {
    console.error('Error confirming repair request fulfillment with callable:', error);
    return rethrowCallableError(error, 'Nie udało się potwierdzić przekazania zgłoszenia.');
  }
};

export interface SubmitFinalQuotePayload {
  diagnosis: {
    description: string;
    cause: string;
    notes?: string;
  };
  finalQuote: {
    amount: number;
    description: string;
    lineItems: FinalRepairQuoteLineItem[];
  };
}

const submitRepairRequestFinalQuote = async (
  requestId: string,
  payload: SubmitFinalQuotePayload
): Promise<void> => {
  type SubmitRepairRequestFinalQuoteCallableInput = SubmitFinalQuotePayload & {
    requestId: string;
  };
  type SubmitRepairRequestFinalQuoteCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'finalQuoteReady';
  };

  const submitFinalQuoteFn = httpsCallable<
    SubmitRepairRequestFinalQuoteCallableInput,
    SubmitRepairRequestFinalQuoteCallableResponse
  >(functions, 'submitRepairRequestFinalQuote');

  try {
    await submitFinalQuoteFn({
      requestId,
      diagnosis: {
        description: payload.diagnosis.description.trim(),
        cause: payload.diagnosis.cause.trim(),
        ...(payload.diagnosis.notes?.trim() ? { notes: payload.diagnosis.notes.trim() } : {}),
      },
      finalQuote: {
        amount: payload.finalQuote.amount,
        description: payload.finalQuote.description.trim(),
        lineItems: payload.finalQuote.lineItems.map((item) => ({
          type: item.type,
          label: item.label.trim(),
          amount: item.amount,
          ...(item.quantity !== undefined ? { quantity: item.quantity } : {}),
          ...(item.notes?.trim() ? { notes: item.notes.trim() } : {}),
        })),
      },
    });
  } catch (error) {
    console.error('Error submitting final quote with callable:', error);
    return rethrowCallableError(error, 'Nie udało się wysłać finalnej wyceny.');
  }
};

const respondToRepairRequestFinalQuote = async (
  requestId: string,
  decision: 'accept' | 'reject',
  rejectionReason?: string
): Promise<void> => {
  type RespondToRepairRequestFinalQuoteCallableInput = {
    requestId: string;
    decision: 'accept' | 'reject';
    rejectionReason?: string;
  };
  type RespondToRepairRequestFinalQuoteCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'finalQuoteAccepted' | 'finalQuoteRejected';
  };

  const respondToFinalQuoteFn = httpsCallable<
    RespondToRepairRequestFinalQuoteCallableInput,
    RespondToRepairRequestFinalQuoteCallableResponse
  >(functions, 'respondToRepairRequestFinalQuote');

  try {
    const normalizedReason = rejectionReason?.trim();
    await respondToFinalQuoteFn({
      requestId,
      decision,
      ...(decision === 'reject' && normalizedReason ? { rejectionReason: normalizedReason } : {}),
    });
  } catch (error) {
    console.error('Error responding to final quote with callable:', error);
    return rethrowCallableError(error, 'Nie udało się zapisać decyzji o finalnej wycenie.');
  }
};

export interface MarkRepairWorkCompletedPayload {
  workSummary: string;
  partsUsedNotes?: string;
  serviceNotes?: string;
}

export interface ConfirmCashSettlementPayload {
  settlementNote?: string;
  handoverNote?: string;
}

const startRepairWork = async (requestId: string): Promise<void> => {
  const startRepairWorkFn = httpsCallable<
    { requestId: string },
    { success: boolean; requestId: string; previousStatus: string; status: 'repairInProgress' }
  >(functions, 'startRepairWork');

  try {
    await startRepairWorkFn({ requestId });
  } catch (error) {
    console.error('Error starting repair work with callable:', error);
    return rethrowCallableError(error, 'Nie udało się rozpocząć naprawy.');
  }
};

const markRepairWorkCompleted = async (
  requestId: string,
  payload: MarkRepairWorkCompletedPayload
): Promise<void> => {
  const markRepairWorkCompletedFn = httpsCallable<
    MarkRepairWorkCompletedPayload & { requestId: string },
    { success: boolean; requestId: string; previousStatus: string; status: 'repairCompleted' }
  >(functions, 'markRepairWorkCompleted');

  try {
    await markRepairWorkCompletedFn({
      requestId,
      workSummary: payload.workSummary.trim(),
      ...(payload.partsUsedNotes?.trim() ? { partsUsedNotes: payload.partsUsedNotes.trim() } : {}),
      ...(payload.serviceNotes?.trim() ? { serviceNotes: payload.serviceNotes.trim() } : {}),
    });
  } catch (error) {
    console.error('Error marking repair work completed with callable:', error);
    return rethrowCallableError(error, 'Nie udało się oznaczyć naprawy jako wykonanej.');
  }
};

const prepareCashSettlement = async (requestId: string): Promise<void> => {
  const prepareCashSettlementFn = httpsCallable<
    { requestId: string },
    {
      success: boolean;
      requestId: string;
      previousStatus: string;
      status: 'awaitingCashSettlement';
      amount: number;
      currency: 'PLN';
    }
  >(functions, 'prepareCashSettlement');

  try {
    await prepareCashSettlementFn({ requestId });
  } catch (error) {
    console.error('Error preparing cash settlement with callable:', error);
    return rethrowCallableError(error, 'Nie udało się przygotować rozliczenia.');
  }
};

const confirmCashSettlement = async (
  requestId: string,
  payload: ConfirmCashSettlementPayload = {}
): Promise<void> => {
  const confirmCashSettlementFn = httpsCallable<
    ConfirmCashSettlementPayload & { requestId: string },
    { success: boolean; requestId: string; previousStatus: string; status: 'completed' }
  >(functions, 'confirmCashSettlement');

  try {
    await confirmCashSettlementFn({
      requestId,
      ...(payload.settlementNote?.trim() ? { settlementNote: payload.settlementNote.trim() } : {}),
      ...(payload.handoverNote?.trim() ? { handoverNote: payload.handoverNote.trim() } : {}),
    });
  } catch (error) {
    console.error('Error confirming cash settlement with callable:', error);
    return rethrowCallableError(error, 'Nie udało się potwierdzić rozliczenia.');
  }
};

const rejectRepairRequestByService = async (requestId: string): Promise<void> => {
  type RejectRepairRequestByServiceCallableInput = {
    requestId: string;
  };
  type RejectRepairRequestByServiceCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'rejected';
  };

  const rejectRepairRequestByServiceFn = httpsCallable<
    RejectRepairRequestByServiceCallableInput,
    RejectRepairRequestByServiceCallableResponse
  >(functions, 'rejectRepairRequestByService');

  try {
    await rejectRepairRequestByServiceFn({ requestId });
  } catch (error) {
    console.error('Error rejecting repair request with callable:', error);
    return rethrowCallableError(error, 'Nie udało się odrzucić zlecenia.');
  }
};

export interface CancelRepairRequestByServicePayload {
  reasonCode: ServiceCancellationReasonCode;
  reasonText?: string;
}

const cancelRepairRequestByService = async (
  requestId: string,
  payload: CancelRepairRequestByServicePayload
): Promise<void> => {
  type CancelRepairRequestByServiceCallableInput = CancelRepairRequestByServicePayload & {
    requestId: string;
  };
  type CancelRepairRequestByServiceCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: 'cancelledByService';
  };

  const cancelRepairRequestByServiceFn = httpsCallable<
    CancelRepairRequestByServiceCallableInput,
    CancelRepairRequestByServiceCallableResponse
  >(functions, 'cancelRepairRequestByService');

  try {
    const normalizedReasonText = payload.reasonText?.trim();
    await cancelRepairRequestByServiceFn({
      requestId,
      reasonCode: payload.reasonCode,
      ...(normalizedReasonText ? { reasonText: normalizedReasonText } : {}),
    });
  } catch (error) {
    console.error('Error cancelling repair request with callable:', error);
    return rethrowCallableError(error, 'Nie udało się zamknąć sprawy bez naprawy.');
  }
};

const scheduleRepairRequestServicePlan = async (
  requestId: string,
  payload: ScheduleRepairRequestServicePlanPayload
): Promise<void> => {
  const normalizedRequestId = normalizeRequestIdForCommand(requestId);
  const normalizedPayload = normalizeScheduleRepairRequestServicePlanPayload(payload);
  const scheduleRepairRequestServicePlanFn = httpsCallable<
    ScheduleRepairRequestServicePlanCallableInput,
    ScheduleRepairRequestServicePlanCallableResponse
  >(functions, 'scheduleRepairRequestServicePlan');

  try {
    await scheduleRepairRequestServicePlanFn({
      requestId: normalizedRequestId,
      ...normalizedPayload,
    });
  } catch (error) {
    console.error('Error scheduling repair request service plan with callable:', error);
    throw mapServicePlannerCallableError(
      error,
      'Nie udało się zapisać terminu pracy. Odśwież widok i spróbuj ponownie.'
    );
  }
};

const clearRepairRequestServicePlan = async (requestId: string): Promise<void> => {
  const normalizedRequestId = normalizeRequestIdForCommand(requestId);
  const clearRepairRequestServicePlanFn = httpsCallable<
    { requestId: string },
    ClearRepairRequestServicePlanCallableResponse
  >(functions, 'clearRepairRequestServicePlan');

  try {
    await clearRepairRequestServicePlanFn({ requestId: normalizedRequestId });
  } catch (error) {
    console.error('Error clearing repair request service plan with callable:', error);
    throw mapServicePlannerCallableError(
      error,
      'Nie udało się wyczyścić terminu pracy. Odśwież widok i spróbuj ponownie.'
    );
  }
};

const executeRepairWorkflowTransition = async (
  requestId: string,
  nextStatus: RepairStatus
): Promise<void> => {
  type ExecuteRepairWorkflowTransitionCallableInput = {
    requestId: string;
    nextStatus: string;
  };
  type ExecuteRepairWorkflowTransitionCallableResponse = {
    success: boolean;
    requestId: string;
    previousStatus: string;
    status: string;
    actor: 'client' | 'service';
  };

  const executeRepairWorkflowTransitionFn = httpsCallable<
    ExecuteRepairWorkflowTransitionCallableInput,
    ExecuteRepairWorkflowTransitionCallableResponse
  >(functions, 'executeRepairWorkflowTransition');

  try {
    await executeRepairWorkflowTransitionFn({ requestId, nextStatus });
  } catch (error) {
    console.error('Error executing post-decision workflow transition:', error);
    return rethrowCallableError(error, 'Nie udało się wykonać akcji workflow.');
  }
};

/**
 * Updates the status of a repair request
 */
const updateRequestStatus = async (
  requestId: string,
  newStatus: RepairStatus,
  extraData?: Partial<RepairRequest>
): Promise<void> => {
  if (newStatus === RepairStatus.CANCELLED_BY_CLIENT) {
    throw new Error(
      'Anulowanie zgłoszenia przez klienta wymaga canonical executeRepairWorkflowTransition.'
    );
  }

  try {
    const requestRef = doc(firestore, 'repairRequests', requestId);
    await updateDoc(requestRef, {
      ...(extraData || {}),
      status: newStatus,
      updatedAt: serverTimestamp(),
      ...(newStatus === RepairStatus.COMPLETED && {
        completedDate: serverTimestamp(),
      }),
    });
  } catch (error) {
    console.error('Error updating request status:', error);
    throw error;
  }
};

/**
 * Validates and performs a workflow transition for a repair request.
 */
const transitionRequestStatus = async (
  requestId: string,
  newStatus: RepairStatus,
  actor: RepairRequestWorkflowActor,
  options?: {
    quotedPrice?: number;
    quoteDescription?: string;
  }
): Promise<void> => {
  const request = await getRequestDetails(requestId);
  const currentStatus = request.status;
  if (!currentStatus) {
    throw new Error('Brak aktualnego statusu zlecenia.');
  }

  const isClientCancellationTarget =
    actor === 'client' && newStatus === RepairStatus.CANCELLED_BY_CLIENT;
  if (isClientCancellationTarget) {
    const isLegacyCancellationSource =
      request.workflowVersion !== 2 &&
      (currentStatus === RepairStatus.PENDING_QUOTE ||
        currentStatus === RepairStatus.ACCEPTED ||
        currentStatus === RepairStatus.IN_PROGRESS);
    const isAllowedClientCancellationSource =
      currentStatus === RepairStatus.NEW || isLegacyCancellationSource;

    if (!isAllowedClientCancellationSource) {
      if (request.workflowVersion === 2) {
        throw new Error('Zgłoszenie V2 można anulować tylko przed rozpoczęciem obsługi.');
      }
      throw new Error(`Klient nie moze zmienic statusu z "${currentStatus}" na "${newStatus}".`);
    }

    await executeRepairWorkflowTransition(requestId, newStatus);
    return;
  }

  const isAllowed = canTransitionRepairRequest(currentStatus, newStatus, actor);
  if (!isAllowed) {
    const action = getTransitionAction(currentStatus, newStatus, actor);
    const actorLabel = actor === 'service' ? 'Serwis' : 'Klient';
    if (!action) {
      throw new Error(
        `${actorLabel} nie moze zmienic statusu z "${currentStatus}" na "${newStatus}".`
      );
    }
  }

  const patch: Partial<RepairRequest> = {};

  if (newStatus === RepairStatus.QUOTED) {
    const rawPrice = options?.quotedPrice;
    const normalizedPrice = typeof rawPrice === 'number' ? Number(rawPrice.toFixed(2)) : NaN;
    if (!Number.isFinite(normalizedPrice) || normalizedPrice <= 0) {
      throw new Error('Aby wyslac wycene, podaj poprawna kwote wieksza od 0.');
    }
    patch.quotedPrice = normalizedPrice;
    patch.quoteDescription = (options?.quoteDescription || '').trim();
    patch.quotedAt = serverTimestamp() as any;
  }

  if (
    actor === 'service' &&
    currentStatus === RepairStatus.PENDING_QUOTE &&
    newStatus === RepairStatus.QUOTED
  ) {
    await submitRepairRequestQuote(
      requestId,
      patch.quotedPrice as number,
      (patch.quoteDescription as string) || ''
    );
    return;
  }

  if (
    actor === 'client' &&
    currentStatus === RepairStatus.QUOTED &&
    (newStatus === RepairStatus.ACCEPTED || newStatus === RepairStatus.REJECTED)
  ) {
    await respondToRepairQuote(
      requestId,
      newStatus === RepairStatus.ACCEPTED ? 'accept' : 'reject'
    );
    return;
  }

  if (
    actor === 'service' &&
    newStatus === RepairStatus.CANCELLED_BY_SERVICE &&
    request.workflowVersion === 2
  ) {
    throw new Error('Zgłoszenie V2 można zamknąć bez naprawy tylko przez dedykowany command.');
  }

  if (
    actor === 'service' &&
    newStatus === RepairStatus.REJECTED &&
    (currentStatus === RepairStatus.NEW || currentStatus === RepairStatus.PENDING_QUOTE)
  ) {
    await rejectRepairRequestByService(requestId);
    return;
  }

  const isPostDecisionServiceTransition =
    actor === 'service' &&
    ((currentStatus === RepairStatus.ACCEPTED &&
      (newStatus === RepairStatus.IN_PROGRESS ||
        newStatus === RepairStatus.PENDING_PAYMENT ||
        newStatus === RepairStatus.CANCELLED_BY_SERVICE)) ||
      (currentStatus === RepairStatus.IN_PROGRESS &&
        (newStatus === RepairStatus.PENDING_PAYMENT ||
          newStatus === RepairStatus.COMPLETED ||
          newStatus === RepairStatus.CANCELLED_BY_SERVICE)) ||
      (currentStatus === RepairStatus.PENDING_PAYMENT && newStatus === RepairStatus.COMPLETED));

  if (
    actor === 'service' &&
    currentStatus === RepairStatus.FINAL_QUOTE_ACCEPTED &&
    newStatus === RepairStatus.REPAIR_IN_PROGRESS
  ) {
    await startRepairWork(requestId);
    return;
  }

  if (
    actor === 'service' &&
    currentStatus === RepairStatus.REPAIR_COMPLETED &&
    newStatus === RepairStatus.AWAITING_CASH_SETTLEMENT
  ) {
    await prepareCashSettlement(requestId);
    return;
  }

  if (isPostDecisionServiceTransition) {
    await executeRepairWorkflowTransition(requestId, newStatus);
    return;
  }

  if (
    actor === 'service' &&
    currentStatus === RepairStatus.NEW &&
    newStatus === RepairStatus.PENDING_QUOTE
  ) {
    await startRepairRequestQuote(requestId);
    return;
  }

  await updateRequestStatus(requestId, newStatus, patch);
};

const markServiceActivityAsRead = async (
  requestId: string,
  section: RepairRequestUnreadSection
): Promise<void> => {
  try {
    type MarkRepairRequestActivityAsReadCallableInput = {
      requestId: string;
      section: RepairRequestUnreadSection;
    };
    type MarkRepairRequestActivityAsReadCallableResponse = {
      success: boolean;
      requestId: string;
      section: RepairRequestUnreadSection;
      actor: 'client' | 'service';
      requestUnreadSections: RepairRequestUnreadSection[];
      syncCleanupCount: number;
    };

    const markRepairRequestActivityAsReadFn = httpsCallable<
      MarkRepairRequestActivityAsReadCallableInput,
      MarkRepairRequestActivityAsReadCallableResponse
    >(functions, 'markRepairRequestActivityAsRead');

    await markRepairRequestActivityAsReadFn({ requestId, section });
  } catch (error) {
    console.error('Error marking service activity as read:', error);
    throw error;
  }
};

const markClientActivityAsRead = async (
  requestId: string,
  section: RepairRequestUnreadSection
): Promise<void> => {
  try {
    type MarkRepairRequestActivityAsReadCallableInput = {
      requestId: string;
      section: RepairRequestUnreadSection;
    };
    type MarkRepairRequestActivityAsReadCallableResponse = {
      success: boolean;
      requestId: string;
      section: RepairRequestUnreadSection;
      actor: 'client' | 'service';
      requestUnreadSections: RepairRequestUnreadSection[];
      syncCleanupCount: number;
    };

    const markRepairRequestActivityAsReadFn = httpsCallable<
      MarkRepairRequestActivityAsReadCallableInput,
      MarkRepairRequestActivityAsReadCallableResponse
    >(functions, 'markRepairRequestActivityAsRead');

    await markRepairRequestActivityAsReadFn({ requestId, section });
  } catch (error) {
    console.error('Error marking client activity as read:', error);
    throw error;
  }
};

/**
 * Gets all repair requests for a service provider
 */
const getRequestsForServiceProvider = async (
  serviceProviderId: string,
  statusFilter: StatusFilter = 'all',
  sortBy: SortByType = 'createdAt'
): Promise<RepairRequest[]> => {
  try {
    const baseCollection = collection(firestore, 'repairRequests');
    const canonicalServiceProviderQuery = query(
      baseCollection,
      where('serviceProviderId', '==', serviceProviderId)
    );
    const canonicalSnapshot = await getDocs(canonicalServiceProviderQuery);
    let requests = snapshotToRequests(canonicalSnapshot);

    if (statusFilter === 'active') {
      const activeStatuses = new Set(getActiveRepairStatuses());
      requests = requests.filter((request) => activeStatuses.has(request.status));
    } else if (statusFilter !== 'all') {
      requests = requests.filter((request) => request.status === statusFilter);
    }

    requests = sortRequests(requests, sortBy);

    console.log(
      `Got ${requests.length} requests for service provider ${serviceProviderId} (filter: ${statusFilter}, sort: ${sortBy})`
    );
    return requests;
  } catch (error) {
    console.error('Error getting requests for service provider:', error);
    throw error;
  }
};

/**
 * Gets all repair requests for a client
 */
const getRequestsForClient = async (clientId: string): Promise<RepairRequest[]> => {
  try {
    const q = query(collection(firestore, 'repairRequests'), where('clientId', '==', clientId));

    const querySnapshot = await getDocs(q);
    const requests = snapshotToRequests(querySnapshot);
    const sorted = sortRequests(requests, 'createdAt');
    console.log(`Got ${sorted.length} requests for client ${clientId}`);
    return sorted;
  } catch (error) {
    console.error('Error getting requests for client:', error);
    throw error;
  }
};

const subscribeToRequestsForClient = (
  clientId: string,
  onNext: (requests: RepairRequest[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const q = query(collection(firestore, 'repairRequests'), where('clientId', '==', clientId));
  return onSnapshot(
    q,
    (querySnapshot) => {
      onNext(sortRequests(snapshotToRequests(querySnapshot), 'createdAt'));
    },
    (error) => {
      console.error('Error subscribing to client repair requests:', error);
      onError?.(error as Error);
    }
  );
};

const subscribeToRequestsForServiceProvider = (
  serviceProviderId: string,
  statusFilter: StatusFilter = 'all',
  sortBy: SortByType = 'createdAt',
  onNext: (requests: RepairRequest[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const baseCollection = collection(firestore, 'repairRequests');
  const canonicalServiceProviderQuery = query(
    baseCollection,
    where('serviceProviderId', '==', serviceProviderId)
  );
  let canonicalRequests: RepairRequest[] = [];
  let unsubscribeCanonical: () => void = () => undefined;

  const emit = () => {
    onNext(applyRequestListView(dedupeRequests(canonicalRequests), statusFilter, sortBy));
  };

  unsubscribeCanonical = onSnapshot(
    canonicalServiceProviderQuery,
    (querySnapshot) => {
      canonicalRequests = snapshotToRequests(querySnapshot);
      emit();
    },
    (error) => {
      console.error(
        'Canonical serviceProviderId subscription failed, continuing with legacy fallback:',
        error
      );
      onError?.(error as Error);
    }
  );

  return () => {
    unsubscribeCanonical();
  };
};

export const repairRequestService = {
  getRequestDetails,
  subscribeToRequestDetails,
  createRepairRequest,
  attachRequestPhotos,
  submitPreliminaryEstimate,
  respondToPreliminaryEstimate,
  confirmRepairRequestFulfillment,
  submitRepairRequestFinalQuote,
  respondToRepairRequestFinalQuote,
  startRepairWork,
  markRepairWorkCompleted,
  prepareCashSettlement,
  confirmCashSettlement,
  cancelRepairRequestByService,
  scheduleRepairRequestServicePlan,
  clearRepairRequestServicePlan,
  updateRequestStatus,
  transitionRequestStatus,
  markServiceActivityAsRead,
  markClientActivityAsRead,
  subscribeToRequestsForServiceProvider,
  subscribeToRequestsForClient,
  getRequestsForServiceProvider,
  getRequestsForClient,
};
